import { FeatureFlag, type EFeatureFlag } from './keys';

/**
 * The feature flag behind each admin page. One table drives both the sidebar
 * and the page itself, so a switched-off feature disappears from the menu and
 * its address shows the "feature disabled" notice, instead of the page loading
 * into a refused request.
 *
 * A path is covered by every prefix it starts with: a page needs its module and
 * its own feature on. The API refuses the six modules, so for them this also
 * keeps the page from loading into a 403.
 */
const FLAG_BY_ROUTE_PREFIX: [prefix: string, flag: EFeatureFlag][] = [
  // Modules
  ['/admin/operations', FeatureFlag.OPERATIONS_MODULE],
  ['/admin/workforce', FeatureFlag.WORKFORCE_MODULE],
  ['/admin/safety', FeatureFlag.SAFETY_MODULE],
  ['/admin/compliance', FeatureFlag.COMPLIANCE_MODULE],
  ['/admin/reports', FeatureFlag.REPORTING_MODULE],
  ['/admin/finance', FeatureFlag.FINANCE_MODULE],
  // Operations
  ['/admin/operations/hierarchy', FeatureFlag.SITE_HIERARCHY],
  ['/admin/operations/production/logs', FeatureFlag.PRODUCTION_LOGS],
  ['/admin/operations/production/targets', FeatureFlag.PRODUCTION_TARGETS],
  ['/admin/operations/development', FeatureFlag.DEVELOPMENT_TRACKING],
  ['/admin/operations/integrity', FeatureFlag.TRACEABILITY_INTEGRITY],
  ['/admin/operations/integrity-report', FeatureFlag.TRACEABILITY_INTEGRITY],
  ['/admin/operations/lots', FeatureFlag.MEU_TRACEABILITY],
  ['/admin/operations/tailings', FeatureFlag.TAILINGS_MANAGEMENT],
  ['/admin/operations/blaster-certificates', FeatureFlag.BLASTER_CERTIFICATES],
  ['/admin/operations/equipment-logs', FeatureFlag.EQUIPMENT_TRACKING],
  ['/admin/config/custody-approval', FeatureFlag.CUSTODY_TRANSFER_APPROVAL],
  // Workforce
  ['/admin/workforce/workers', FeatureFlag.WORKER_DIRECTORY],
  ['/admin/workforce/attendance', FeatureFlag.ATTENDANCE_TRACKING],
  ['/admin/workforce/worker-approvals', FeatureFlag.WORKER_APPROVALS],
  ['/admin/workforce/labour-tracking', FeatureFlag.TEAM_LABOUR_TRACKING],
  ['/admin/workforce/compensation', FeatureFlag.PAYROLL_COMPENSATION],
  // Safety
  ['/admin/safety/incidents', FeatureFlag.INCIDENT_MANAGEMENT],
  ['/admin/safety/inspections', FeatureFlag.INSPECTION_MANAGEMENT],
  ['/admin/safety/hazards', FeatureFlag.HAZARD_REGISTER],
  ['/admin/safety/environment/compliance', FeatureFlag.ENVIRONMENTAL_COMPLIANCE],
  // Compliance
  ['/admin/compliance/licenses', FeatureFlag.LICENSE_MANAGEMENT],
  ['/admin/compliance/requirements', FeatureFlag.COMPLIANCE_REQUIREMENTS_TRACKING],
  ['/admin/compliance/documents', FeatureFlag.COMPLIANCE_DOCUMENTS],
  ['/admin/compliance/inspections', FeatureFlag.INSPECTION_FEEDBACK],
  ['/admin/compliance/reports', FeatureFlag.REPORTING_MODULE],
  // Inventory
  ['/admin/inventory/equipment', FeatureFlag.EQUIPMENT_TRACKING],
  ['/admin/inventory/materials', FeatureFlag.MATERIAL_MANAGEMENT],
  ['/admin/inventory/categories', FeatureFlag.MATERIAL_MANAGEMENT],
  ['/admin/inventory/movements', FeatureFlag.MATERIAL_MANAGEMENT],
  ['/admin/inventory/stock-on-hand', FeatureFlag.MATERIAL_MANAGEMENT],
  ['/admin/inventory/counts', FeatureFlag.MATERIAL_MANAGEMENT],
  ['/admin/inventory/reconciliation', FeatureFlag.MATERIAL_MANAGEMENT],
  ['/admin/inventory/requisitions', FeatureFlag.MATERIAL_REQUISITIONS],
  ['/admin/inventory/stockpile', FeatureFlag.ORE_STOCKPILE_TRACKING],
  // Finance and configuration
  ['/admin/finance/batches', FeatureFlag.PAYMENT_BATCHES],
  ['/admin/config/workflows', FeatureFlag.WORKFLOW_CONFIGURATION],
];

const startsWithPath = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Every flag that has to be on for this page to be available. */
export function flagsForPath(pathname: string): EFeatureFlag[] {
  return FLAG_BY_ROUTE_PREFIX.filter(([prefix]) => startsWithPath(pathname, prefix)).map(
    ([, flag]) => flag,
  );
}
